import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import Header from "../component/Header";
import ExploreResultLocations from "../component/ExploreResultLocations";
import Footer from "../component/Footer";
import { Link } from "react-router-dom";
import TooltipIconOne from '../assets/images/tooltip-2.svg'
import AgentImg from '../assets/images/agent-img.png'
import { useNavigate } from "react-router-dom";
function MortgageCalculator() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    homePrice: 750000,
    downPayment: 150000,
    downPaymentPercent: 20,
    loanTerm: 30,
    interestRate: 6.89,
    propertyTaxes: 0,
    homeInsurance: 0,
    hoaFees: 0,
    pmi: 0,
  });

  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [paymentBreakdown, setPaymentBreakdown] = useState({
    principal: 0,
    propertyTaxes: 0,
    homeInsurance: 0,
    hoa: 0,
    pmi: 0,
  });
  const [zipCode, setZipCode] = useState("");

  // Calculate mortgage payment
  useEffect(() => {
    calculateMortgage();
  }, [formData]);

  const calculateMortgage = () => {
    const principal = formData.homePrice - formData.downPayment;
    const monthlyInterestRate = formData.interestRate / 100 / 12;
    const numberOfPayments = formData.loanTerm * 12;

    // Calculate monthly principal and interest
    let monthlyPI = 0;
    if (monthlyInterestRate === 0) {
      monthlyPI = principal / numberOfPayments;
    } else {
      monthlyPI =
        (principal *
          (monthlyInterestRate *
            Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
    }

    const monthlyTaxes = 607;
    const monthlyInsurance = 72;
    const monthlyHOA = 0;
    const monthlyPMI = formData.pmi;

    const total =
      monthlyPI + monthlyTaxes + monthlyInsurance + monthlyHOA + monthlyPMI;

    setMonthlyPayment(total);
    setPaymentBreakdown({
      principal: monthlyPI,
      propertyTaxes: monthlyTaxes,
      homeInsurance: monthlyInsurance,
      hoa: monthlyHOA,
      pmi: monthlyPMI,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let numValue = parseFloat(value) || 0;

    if (name === "homePrice" || name === "downPayment") {
      setFormData((prev) => {
        const newData = { ...prev, [name]: numValue };
        if (name === "homePrice") {
          newData.downPayment = (numValue * prev.downPaymentPercent) / 100;
        } else if (name === "downPayment") {
          newData.downPaymentPercent = (numValue / prev.homePrice) * 100;
        }
        return newData;
      });
    } else if (name === "downPaymentPercent") {
      setFormData((prev) => ({
        ...prev,
        downPaymentPercent: numValue,
        downPayment: (prev.homePrice * numValue) / 100,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: numValue }));
    }
  };

  const handleReset = () => {
    setFormData({
      homePrice: 750000,
      downPayment: 150000,
      downPaymentPercent: 20,
      loanTerm: 30,
      interestRate: 6.89,
      propertyTaxes: 607,
      homeInsurance: 72,
      hoaFees: 0,
      pmi: 0,
    });
    setZipCode("");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <>
      <div className="main mortgage-calculator-page">
        <Header />
        <section className="mortgage-calculator-section common-padding">
          <Container>
            <div className="common-header text-left">
              <h2>Mortgage calculator</h2>
              <p>Estimate your mortgage payment, including the principal and
                interest, taxes, insurance, HOA, and PMI. Add your location
                for more accurate estimates.</p>
            </div>
            
            <Row>
              {/* Left Column - Calculator Form */}
              <Col lg={5}>
                <div className="calculator-form">
                  <Form>
                    {/* Home Price */}
                    <Form.Group className="mb-4">
                      <Form.Label>Home price</Form.Label>
                      <div className="input-with-currency">
                        <span className="currency-symbol">$</span>
                        <Form.Control
                          type="tel"
                          name="homePrice"
                          value={formData.homePrice}
                          onChange={handleInputChange}
                        />
                      </div>
                      <Form.Range
                        min="0"
                        max="2000000"
                        step="10000"
                        value={formData.homePrice}
                        onChange={(e) => handleInputChange({ target: { name: 'homePrice', value: e.target.value }})}
                        className="custom-range mb-3"
                        style={{ '--value': (formData.homePrice / 2000000) * 100 }}
                      />
                       <Form.Text className="text-muted">
                        The amount you plan to offer for a home.
                      </Form.Text>
                    </Form.Group>

                    {/* Down Payment */}
                    <Form.Group className="mb-4">
                      <Form.Label>Down payment</Form.Label>
                      <Row>
                        <Col xs={8} md={9}>
                          <div className="input-with-currency">
                            <span className="currency-symbol">$</span>
                            <Form.Control
                              type="tel"
                              name="downPayment"
                              value={formData.downPayment}
                              onChange={handleInputChange}
                            />
                          </div>
                        </Col>
                        <Col xs={4} md={3}>
                          <div className="input-with-percent">
                            <Form.Control
                              type="tel"
                              name="downPaymentPercent"
                              value={Math.round(formData.downPaymentPercent)}
                              onChange={handleInputChange}
                            />
                            <span className="percent-symbol">%</span>
                          </div>
                        </Col>
                      </Row>
                      <Form.Range
                        min="0"
                        max={formData.homePrice}
                        step="1000"
                        value={formData.downPayment}
                        onChange={(e) => handleInputChange({ target: { name: 'downPayment', value: e.target.value }})}
                        className="custom-range mb-3"
                        style={{ '--value': (formData.downPayment / formData.homePrice) * 100 }}
                      />
                      <Form.Text className="text-muted">
                       Cash you can pay when you close.
                      </Form.Text>
                    </Form.Group>

                   
                   
                    {/* Loan Term */}
                    <Form.Group className="mb-3">
                      <Form.Label>Loan type</Form.Label>
                      <Form.Select
                        name="loanTerm"
                        value={formData.loanTerm}
                        onChange={handleInputChange}
                      >
                        <option value="30">30-year fixed</option>
                        <option value="20">20-year fixed</option>
                        <option value="15">15-year fixed</option>
                        <option value="10">10-year fixed</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Affects interest rates. 30- or 15-year loans are standard.
                      </Form.Text>
                    </Form.Group>

                    {/* Interest Rate */}
                    <Form.Group className="mb-3">
                      <Form.Label>Mortgage interest rate</Form.Label>
                      <Form.Select
                        name="interestRate"
                        value={formData.interestRate}
                        onChange={handleInputChange}
                      >
                        <option value="30">30-year fixed</option>
                        <option value="20">20-year fixed</option>
                        <option value="15">15-year fixed</option>
                        <option value="10">10-year fixed</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Varies depending on lender and credit score.
                      </Form.Text>
                    </Form.Group>

                  </Form>
                </div>
              </Col>

              {/* Right Column - Results */}
              <Col lg={7}>
                <div className="calculator-results">
                  {/* Monthly Payment Card */}
                  {/* Monthly Payment Card */}
                  <Card className="payment-card mb-4">
                    <Card.Body className="p-0">
                      <div className="d-flex justify-content-between align-items-start mb-4">
                        <div>
                          <h3 className="payment-amount mb-0">
                            {formatCurrency(monthlyPayment)}{" "} per month
                          </h3>
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <p className="mb-0">
                              {formData.loanTerm}-year fixed,{" "}
                              {formData.interestRate}% interest
                            </p>
                            <span className="info-icon">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="prequalified-tooltip">
                                    Get prequalified to see how much you can afford and strengthen your offer.
                                  </Tooltip>
                                }
                              >
                                <img src={TooltipIconOne} alt="" />
                              </OverlayTrigger>
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="link"
                          className="reset-btn p-0"
                          onClick={handleReset}
                        >
                          Reset
                        </Button>
                      </div>

                      {/* Progress Bar */}
                      <div className="payment-progress-bar mb-4">
                        <div className="progress-segments">
                          <div
                            className="segment principal"
                            style={{
                              width: `${
                                (paymentBreakdown.principal / monthlyPayment) *
                                100
                              }%`,
                            }}
                          />
                          <div
                            className="segment property-tax"
                            style={{
                              width: `${
                                (paymentBreakdown.propertyTaxes /
                                  monthlyPayment) *
                                100
                              }%`,
                            }}
                          />
                          <div
                            className="segment hoa"
                            style={{
                              width: `${
                                (paymentBreakdown.hoa / monthlyPayment) * 100
                              }%`,
                            }}
                          />
                          <div
                            className="segment insurance"
                            style={{
                              width: `${
                                (paymentBreakdown.homeInsurance /
                                  monthlyPayment) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Payment Breakdown */}
                      <div className="payment-breakdown">
                        <div className="breakdown-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <span className="color-indicator principal"></span>
                            <span>Principal and interest</span>
                          </div>
                          <strong className="amount">
                            {formatCurrency(paymentBreakdown.principal)}
                          </strong>
                        </div>

                        <div className="breakdown-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <span className="color-indicator property-tax"></span>
                            <span>Property taxes</span>
                          </div>
                          <strong className="amount text-info">
                            {formatCurrency(paymentBreakdown.propertyTaxes)}
                          </strong>
                        </div>

                        <div className="breakdown-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <span className="color-indicator hoa"></span>
                            <span>HOA dues</span>
                          </div>
                          <strong className="amount text-info">
                            {formatCurrency(paymentBreakdown.hoa)}
                          </strong>
                        </div>

                        <div className="breakdown-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <span className="color-indicator insurance"></span>
                            <span>Home insurance</span>
                          </div>
                          <strong className="amount text-info">
                            {formatCurrency(paymentBreakdown.homeInsurance)}
                          </strong>
                        </div>
                      </div>

                     
<Link  className="custom-bttn style-1" to={"/get-pre-qualified"}>Get Prequalified</Link>
                      
                    </Card.Body>
                  </Card>

                  {/* Agent Card */}
                  <Card className="agent-card">
                    <Card.Body className="p-0">
                       <img
                          src={AgentImg}
                          alt="Agent"
                          className="agent-photo rounded-circle"
                        />
                        <div className="d-flex align-items-start flex-column">
                          <h5>
                            Planning to buy a home? Get expert advice from a Real
                            Estate agent
                          </h5>
                          <p>
                            Our experienced local agents can answer your questions
                            and guide you through strategies to afford the home you
                            want.
                          </p>
                          <Button className="custom-bttn style-3" onClick={() => navigate('/find-an-agent')}>
                            Find an Agent
                          </Button>
                        </div>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>

            {/* Calculating Your Mortgage Payment Section */}
            <Row className="cmp-box">
              <Col lg={12}>
                  <h3>Calculating your mortgage payment</h3>
                  <p>Jambobay mortgage calculator estimates your monthly mortgage payment based on a number of factors. Your mortgage payment includes your principal and interest, down payment, loan term, homeowners insurance, property taxes, and HOA fees. This gives you the ability to compare a number of different home loan scenarios and how it will impact your budget.</p>
              </Col>
            </Row>
          </Container>
        </section>

        <ExploreResultLocations />
        <Footer />
      </div>
    </>
  );
}

export default MortgageCalculator;
